import { Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { UserManagementService } from '../../services/user-management.service';
import { UserHeaders, UserProps } from '../../enums/user-headers';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ISearchUsers, ISearchUsersResponse } from '../../interfaces/isearchUsers';
import { IDistrict, IRoles } from '@core/interfaces/ilookup';
import { LookupService } from '@core/services/lookup.service';
import { distinctUntilChanged, take, tap } from 'rxjs';
import { Role } from '@core/enums/roles';
import { IUser } from '@core/interfaces/iuser';
import { ToastService } from '@core/services/toast.service';
import { NzModalService } from 'ng-zorro-antd/modal';
import { AuthService } from '@core/services/auth.service';
import { NameValidator } from '@core/validators/name.validator';
import { Router } from '@angular/router';
import { IColumnSort } from '@core/interfaces/itable';
import { CustomValidators } from '@core/validators/field.validator';
import { EncryptionService } from '@core/services/encryption.service';
import { AccessibleConfirmService } from '@views/features/course-modules/services/accessible-confirm.service';

@Component({
  selector: 'prism-user-management',
  templateUrl: './user-management.component.html',
  styleUrl: './user-management.component.scss'
})
  export class UserManagementComponent implements OnInit, OnDestroy {
    private userManagementService = inject(UserManagementService);
    private authService = inject(AuthService);
    private lookupService= inject(LookupService);
    private toastService= inject(ToastService);
    private modal= inject(NzModalService);
    private fb = inject(FormBuilder);
    private nameValidator = inject(NameValidator);
    public router = inject(Router);
    private encryptionService = inject(EncryptionService);
    private accessibleConfirm = inject(AccessibleConfirmService);
    private userIdToReset: number | null = null;

    columns = ['fullName', 'emailName', 'userTypeName', 'schoolName', 'districtName'];
    data: any[] = [];
    colHeaders = UserHeaders;
    UserProps = UserProps;
    isVisible = false;
    isPasswordResetVisible = false;
    userForm!: FormGroup;
    searchForm!: FormGroup;
    roles = Role;
    roleOptions: IRoles[] = [];
    organizationOptions: IDistrict[] = [];
    modalTitle = 'Add User';
    pageIndex = 1;
    pageSize = 10;
    totalData = 0;
    currentUser = this.authService.getCurrentUser();
    isDistrictAdmin = this.currentUser.roleId === Role.DistrictAdmin;
    isUser = this.currentUser.roleId === Role.User;
    currentDistrict: IDistrict | null = this.authService.getCurrentDistrict() || null;
    isEditModalSubmitting = false;

    @ViewChild('editUserCloseButton') editUserCloseButton?: ElementRef<HTMLButtonElement>;
    @ViewChild('resetPasswordCloseButton') resetPasswordCloseButton?: ElementRef<HTMLButtonElement>;
    private previouslyFocusedElement: HTMLElement | null = null;

    get firstName() {
      return this.userForm.get('firstName');
    }
    get lastName() {
      return this.userForm.get('lastName');
    }
    get gradeIds() {
      return this.userForm.get('gradeIds');
    }
    get emailName() {
      return this.userForm.get('emailName');
    }
    get roleId() {
      return this.userForm.get('roleId');
    }
    get searchRoleId() {
      return this.searchForm.get('roleId');
    }
    get districtId() {
      return this.userForm.get('districtIds');
    }
    get schoolId() {
      return this.userForm.get('schoolId');
    }
    get search() {
      return this.searchForm.get('search');
    }
    get organizationId() {
      return this.searchForm.get('organizationId');
    }


  constructor(private eRef: ElementRef) {}

  ngOnInit(): void {
    this.initializeForms();
    this.searchUsers();
    this.getOrganizations();
    this.listenUserType();
    this.listenOrganization();
    this.listenRole();
  }

  initializeForms(): void {
    this.userForm = this.fb.group({
      id: [0],
      firstName: [null, [Validators.required, Validators.maxLength(50), this.nameValidator.noNumeric()]],
      lastName: [null, [Validators.required, Validators.maxLength(50), this.nameValidator.noNumeric()]],
      gradeIds: [[], Validators.required],
      emailName: [null, [Validators.required, Validators.email, CustomValidators.noWhitespace(), CustomValidators.blacklistEmail()]],
      roleId: [null, Validators.required],
      districtIds: [[], Validators.required],
      schoolId: [null, Validators.required],
    });

    this.searchForm = this.fb.group({
      search: [''],
      organizationId: [this.currentUser.roleId == Role.DistrictAdmin ? this.currentDistrict?.id : null],
      roleId: [null],
      sortDirection: ['asc'],
      sortColumn: [null]
    });
  }

  requiredIfRoleSelectedValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        const roleId = this.roleId?.value;

        if (roleId !== Role.SystemAdmin) {
            return { 'required': true };
        }

        return null;
    };
  }

  getOrganizations(): void {
    this.lookupService.getOrganizations().pipe(
      take(1),
      tap((res: IDistrict[]) => this.organizationOptions = res)
    ).subscribe();
  }

  searchUsers() {
    let payload: ISearchUsers = {
      ...this.searchForm.value,
      pageIndex: this.pageIndex,
      itemsPerPage: this.pageSize
    };

    this.userManagementService.getUsers(payload).pipe(
      take(1)
    ).subscribe((res: ISearchUsersResponse) => {
      this.data = res.items;
      this.pageIndex = res.pageIndex;
      this.totalData = res.totalData;
    })
  }

  editUser(user: IUser) {
    this.modalTitle = 'Edit User';
    const isUserRole = user.roleId === this.roles.User;
    this.userForm.patchValue({
      ...user,
      districtIds: isUserRole
        ? (user.districtIds?.length ? user.districtIds[0] : [])
        : (user.districtIds?.length ? user.districtIds : [])
    });

    this.applyRoleValidation(user.roleId);

    this.previouslyFocusedElement = document.activeElement as HTMLElement;
    this.isVisible = true;
    this.lockBodyScroll(true);
    setTimeout(() => this.editUserCloseButton?.nativeElement.focus());
  }

  deleteUser(user: IUser) {
    this.modal.confirm({
      nzTitle: `Delete User: ${user.fullName}`,
      nzContent: 'Are you sure you want to delete this user?',
      nzOkText: 'Yes',
      nzIconType: "exclamation-circle",
      nzOnOk: () => {
        this.userManagementService.deleteUser(user.id).pipe(
          tap((res: boolean) => {
            if (res) {
              this.toastService.success('User deleted successfully');
              this.searchUsers();
            }
          })
        ).subscribe();
      },
      nzCancelText: 'No',
    });
  }

  resetUserPassword(user: IUser) {
    this.previouslyFocusedElement = document.activeElement as HTMLElement;
    this.modalTitle = `Reset Password: ${user.fullName}`;
    this.userIdToReset = user.id;
    this.isPasswordResetVisible = true;
    this.lockBodyScroll(true);
    setTimeout(() => this.resetPasswordCloseButton?.nativeElement?.focus());
  }

  applyRoleValidation(roleId: number): void {
    if (roleId === Role.SystemAdmin) {
      this.districtId?.clearValidators();
      this.schoolId?.clearValidators();
      this.schoolId?.setValue(null);
    } else {
      this.districtId?.setValidators([Validators.required]);
      if (roleId === Role.User) {
        this.schoolId?.setValidators([Validators.required]);
        const currentDistrictIds = this.districtId?.value;
        if (Array.isArray(currentDistrictIds) && currentDistrictIds.length > 1) {
          this.districtId?.setValue([currentDistrictIds[0]]);
        }
      } else {
        this.schoolId?.clearValidators();
        this.schoolId?.setValue(null);
        this.gradeIds?.clearValidators();
        this.gradeIds?.setValue(null);
      }
    }

    this.districtId?.updateValueAndValidity();
    this.schoolId?.updateValueAndValidity();
    this.gradeIds?.updateValueAndValidity();
  }

  listenUserType(): void {
    this.roleId?.valueChanges.subscribe((roleId: number) => {
      this.applyRoleValidation(roleId);
    });
  }


  listenOrganization(): void {
    this.organizationId?.valueChanges.pipe(
      distinctUntilChanged(),
      tap((value: number) => {
        const selectedDistrict = this.organizationOptions.find(o => o.id === value) || null;
        this.organizationId?.setValue(value);
        this.authService.setCurrentDistrict(selectedDistrict);
        this.searchUsers();
      })
    ).subscribe();

    this.districtId?.valueChanges.subscribe(() => {
      const districtIdControl = this.districtId;
      if (districtIdControl && districtIdControl.hasError('required')) {
          if (districtIdControl.value !== null && districtIdControl.value !== '' && districtIdControl.value.length > 0) {
              districtIdControl.setErrors(null);
          }
      }
    });
  }

    listenRole(): void {
    this.searchRoleId?.valueChanges.pipe(
      distinctUntilChanged(),
      tap((value: number) => {
        this.searchForm.patchValue({ roleId: value|| null });
        this.searchUsers();
      })
    ).subscribe();
  }

  handlePageIndexChange(pageIndex: number): void {
    this.pageIndex = pageIndex;
    this.searchUsers();
  }

  handlePageSizeChange(pageSize: number): void {
    if (pageSize == null || pageSize === this.pageSize) return;
    this.pageSize = pageSize;
    this.searchUsers();
  }

  handleColumnDirectionChange(columnSort: IColumnSort): void {
    this.searchForm.patchValue({ sortDirection: columnSort.direction, sortColumn: columnSort.column });
    this.searchUsers();
  }

  handleOk(): void {
    this.isEditModalSubmitting = true;
    if (this.userForm.invalid) {
      this.toastService.error('Please fill out all required fields');
      this.isEditModalSubmitting = false;
      return;
    }
    const payload: IUser = {
      ...this.userForm.value
    }
    if (!Array.isArray(payload.districtIds)) {
      payload.districtIds = [payload.districtIds];
    }

    if (this.roleId?.value === Role.SystemAdmin) {
      payload.districtIds = [];
    }

    if(payload.id > 0) {
      this.userManagementService.editUser(payload).pipe(
        take(1),
        tap((res: boolean) => {
          if (res) {
            if (payload.id === this.currentUser?.userId) {
              this.toastService.success('Updated personal account successfully. Please login again.');
              this.authService.logout();
              this.isEditModalSubmitting = false;
              return;
            }
            this.searchUsers();
            this.toastService.success('User updated successfully');
          }
          this.isEditModalSubmitting = false;
          this.isVisible = false;
          this.lockBodyScroll(false);
          this.restorePreviousFocus();
        })
      ).subscribe();
    }
  }

  handleCancel(): void {
    this.isVisible = false;
    this.lockBodyScroll(false);
    this.restorePreviousFocus();
  }

  onDialogKeydown(event: KeyboardEvent, dialog: HTMLElement): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.handleCancel();
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const focusableElements = this.getFocusableElements(dialog);

    if (!focusableElements.length) {
      event.preventDefault();
      dialog.focus();
      return;
    }

    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstFocusableElement) {
      event.preventDefault();
      lastFocusableElement.focus();
      return;
    }

    if (!event.shiftKey && document.activeElement === lastFocusableElement) {
      event.preventDefault();
      firstFocusableElement.focus();
    }
  }

  private restorePreviousFocus(): void {
    setTimeout(() => {
      this.previouslyFocusedElement?.focus();
      this.previouslyFocusedElement = null;
    });
  }

  private lockBodyScroll(lock: boolean): void {
    document.body.style.overflow = lock ? 'hidden' : '';
  }

  ngOnDestroy(): void {
    this.lockBodyScroll(false);
  }

  private getFocusableElements(container: HTMLElement): HTMLElement[] {
    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    return Array.from(container.querySelectorAll<HTMLElement>(focusableSelector)).filter(
      (element) => !element.hasAttribute('disabled') && !element.getAttribute('aria-hidden')
    );
  }
  
  handleCancelPassword(): void {
    this.isPasswordResetVisible = false;
    this.userIdToReset = null;
    this.lockBodyScroll(false);
    this.restorePreviousFocus();
  }

 private performAdminReset(password: string) {
  if (!this.userIdToReset) return;

  const encrypted = this.encryptionService.encryptPassword(password);
  this.authService.resetPassword({
    userId: this.userIdToReset,
    password: encrypted
  })
  .pipe(take(1))
  .subscribe((ok: boolean) => {
    if (ok) {
      this.toastService.success('Password reset successfully');
      this.isPasswordResetVisible = false;
      this.userIdToReset = null;
      this.lockBodyScroll(false);
      this.restorePreviousFocus();
    } else {
      this.toastService.error('Unable to reset password');
    }
  });
}

  submitAdminReset(password: string) {
    const userName = this.modalTitle?.startsWith('Reset Password:')
        ? this.modalTitle.replace('Reset Password:', '').trim()
        : 'this user';

    this.accessibleConfirm.confirm({
      nzTitle: `Reset password for ${userName}?`,
      nzContent: `This will immediately update the user's password.`,
      nzOkText: 'Yes, reset',
      nzOkDanger: false,
      nzOkType: 'primary',
      nzCancelText: 'Cancel',
      nzOnOk: () => this.performAdminReset(password)
    });
  }

    downloadReport(): void {
    const payload: ISearchUsers = {
      ...this.searchForm.value,
      pageIndex: this.pageIndex,
      itemsPerPage: this.pageSize
    };

    this.userManagementService.downloadUsersReport(payload)
      .pipe(take(1))
      .subscribe((res: any) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(res.body);
        a.setAttribute('download', 'prism_user_report.xlsx');
        a.click();
      });
  }

}
